import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { Calendar, ArrowLeft, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SVGMapViewer } from "@/components/SVGMapViewer";
import { StormTypeLegend } from "@/components/StormTypeBadge";
import { LightningActivityIndex } from "@/components/LightningActivityIndex";
import { ThreatBreakdown } from "@/components/ThreatIndicators";
import { SnowfallTotals, SeverePhenomena } from "@/components/SnowfallInfo";
import { formatValidityPeriod } from "@/lib/utils";

export default function OutlookPage() {
  const { date } = useParams();
  const navigate = useNavigate();
  const [outlook, setOutlook] = useState(null);
  const [prevSlug, setPrevSlug] = useState(null);
  const [nextSlug, setNextSlug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (date) {
      fetchOutlook(date);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const fetchOutlook = async (slug) => {
    try {
      setLoading(true);
      setError(null);

      // Determine if slug is a date (YYYY-MM-DD) or a UUID
      const isDate = /^\d{4}-\d{2}-\d{2}$/.test(slug);

      let data;
      if (isDate) {
        const { data: d, error: e } = await supabase
          .from("outlooks")
          .select("*")
          .eq("outlook_date", slug)
          .maybeSingle();
        if (e) throw e;
        data = d;
      } else {
        const { data: d, error: e } = await supabase
          .from("outlooks")
          .select("*")
          .eq("id", slug)
          .maybeSingle();
        if (e) throw e;
        data = d;
      }

      if (!data) {
        setError("Outlook not found");
        return;
      }

      setOutlook(data);

      // Fetch prev and next outlooks by date
      if (data.outlook_date) {
        const [{ data: prevData }, { data: nextData }] = await Promise.all([
          supabase
            .from("outlooks")
            .select("id, outlook_date")
            .eq("is_published", true)
            .lt("outlook_date", data.outlook_date)
            .order("outlook_date", { ascending: false })
            .limit(1)
            .maybeSingle(),
          supabase
            .from("outlooks")
            .select("id, outlook_date")
            .eq("is_published", true)
            .gt("outlook_date", data.outlook_date)
            .order("outlook_date", { ascending: true })
            .limit(1)
            .maybeSingle(),
        ]);

        setPrevSlug(prevData ? prevData.outlook_date || prevData.id : null);
        setNextSlug(nextData ? nextData.outlook_date || nextData.id : null);
      }
    } catch (err) {
      setError("Failed to load outlook");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#FBBF24] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#9CA3AF] text-sm uppercase tracking-wider">Loading outlook...</p>
        </div>
      </div>
    );
  }

  if (error || !outlook) {
    return (
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16">
        <div className="text-center py-20 border border-white/10 bg-[#111827]">
          <h2 className="font-heading text-2xl text-[#F9FAFB] uppercase tracking-wide mb-4">
            {error || "Outlook Not Found"}
          </h2>
          <p className="text-[#9CA3AF] mb-6">
            The requested outlook could not be found.
          </p>
          <div className="flex justify-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="border-white/10 hover:border-[#FBBF24]/30 rounded-none"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
            <Link to="/archive">
              <Button className="bg-[#FBBF24] text-[#1a1a1a] hover:bg-[#F59E0B] font-bold uppercase tracking-wider rounded-none">
                View Archive
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const displayDate = outlook.outlook_date
    ? new Date(outlook.outlook_date + "T00:00:00").toLocaleDateString("en-GB", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : new Date(outlook.created_at).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

  return (
    <div className="animate-fade-in" data-testid="outlook-page">
      {/* Breadcrumb */}
      <div className="border-b border-white/5 py-3">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center gap-2 text-xs">
            <Link to="/" className="text-[#9CA3AF] hover:text-[#FBBF24] transition-colors uppercase tracking-wider">
              Home
            </Link>
            <span className="text-[#6B7280]">/</span>
            <Link to="/archive" className="text-[#9CA3AF] hover:text-[#FBBF24] transition-colors uppercase tracking-wider">
              Archive
            </Link>
            <span className="text-[#6B7280]">/</span>
            <span className="text-[#F9FAFB] font-mono truncate max-w-[240px]">
              {outlook.outlook_date || outlook.id}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="container mx-auto px-4 md:px-8 max-w-7xl py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left – SVG Map */}
          <div className="lg:col-span-8 flex flex-col gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-3 text-xs text-[#9CA3AF]">
                <span className="font-mono flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {displayDate}
                </span>
                {outlook.is_active && (
                  <span className="flex items-center gap-1 text-[#22C55E] font-bold uppercase tracking-widest">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#22C55E] animate-pulse" />
                    Active
                  </span>
                )}
              </div>
              <h1
                className="font-heading text-xl md:text-2xl font-bold text-[#F9FAFB] uppercase tracking-tight"
                data-testid="outlook-headline"
              >
                {outlook.headline || outlook.title}
              </h1>
            </div>

            <SVGMapViewer svgContent={outlook.svg_content} className="w-full" />

            {/* Full discussion text */}
            {(outlook.discussion || outlook.description) && (
              <div className="mt-2 bg-[#111827] border border-white/10 p-5 space-y-3">
                <h3 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                  Forecaster Discussion
                </h3>
                <div className="space-y-3">
                  {(outlook.discussion || outlook.description).split(/\n\n+/).map((para, idx) => (
                    <p key={idx} className="text-[#9CA3AF] text-sm leading-relaxed">
                      {para}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              {prevSlug ? (
                <Link
                  to={`/outlook/${prevSlug}`}
                  className="flex items-center gap-1.5 text-xs text-[#9CA3AF] hover:text-[#FBBF24] transition-colors uppercase tracking-wider"
                  data-testid="prev-outlook-link"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Link>
              ) : (
                <span />
              )}

              <Link
                to="/archive"
                className="text-xs text-[#6B7280] hover:text-[#F9FAFB] transition-colors uppercase tracking-wider"
                data-testid="back-to-archive-link"
              >
                Back to Archive
              </Link>

              {nextSlug ? (
                <Link
                  to={`/outlook/${nextSlug}`}
                  className="flex items-center gap-1.5 text-xs text-[#9CA3AF] hover:text-[#FBBF24] transition-colors uppercase tracking-wider"
                  data-testid="next-outlook-link"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Link>
              ) : (
                <span />
              )}
            </div>
          </div>

          {/* Right – Info Panel */}
          <div className="lg:col-span-4 space-y-4">
            {outlook.type !== "snowfall" ? (
              <>
                {/* Thunderstorm Types */}
                {outlook.storm_types && outlook.storm_types.length > 0 && (
                  <div className="bg-[#111827] border border-white/10 p-4 space-y-3">
                    <h3 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                      Thunderstorm Types
                    </h3>
                    <StormTypeLegend types={outlook.storm_types} />
                  </div>
                )}

                {/* Lightning Activity Index */}
                {outlook.lightning_activity != null && (
                  <div className="bg-[#111827] border border-white/10 p-4">
                    <LightningActivityIndex value={outlook.lightning_activity ?? 0} />
                  </div>
                )}

                {/* Threat Breakdown */}
                <div className="bg-[#111827] border border-white/10 p-4">
                  <ThreatBreakdown outlook={outlook} />
                </div>
              </>
            ) : (
              <>
                {/* Severe Phenomena */}
                {outlook.severe_phenomena && outlook.severe_phenomena.length > 0 && (
                  <div className="bg-[#111827] border border-white/10 p-4">
                    <SeverePhenomena phenomena={outlook.severe_phenomena} />
                  </div>
                )}

                {/* Snowfall Totals */}
                {outlook.snowfall_totals && outlook.snowfall_totals.length > 0 && (
                  <div className="bg-[#111827] border border-white/10 p-4">
                    <SnowfallTotals totals={outlook.snowfall_totals} />
                  </div>
                )}
              </>
            )}

            {/* Validity Period */}
            {formatValidityPeriod(outlook.valid_from, outlook.valid_to) && (
              <div className="bg-[#111827] border border-white/10 p-4 space-y-2">
                <h3 className="font-heading text-xs uppercase tracking-widest text-[#6B7280] flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  Validity
                </h3>
                <p className="font-mono text-sm text-[#F9FAFB]">
                  {formatValidityPeriod(outlook.valid_from, outlook.valid_to)}
                </p>
              </div>
            )}

            {/* Published Date */}
            <div className="bg-[#111827] border border-white/10 p-4 space-y-2">
              <h3 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                Published
              </h3>
              <p className="font-mono text-sm text-[#F9FAFB]">{displayDate}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
